import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Skeleton from '@mui/material/Skeleton';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { styled } from '@mui/material/styles';
import { useApiUrl } from '../../../../../../../shared/hooks/useApiUrl';
import { IImageThumbnail } from '../../../Entities';

type CompareOverlayProps = {
    open: boolean;
    onClose: () => void;
    images: IImageThumbnail[];
    selectedImageIds: Set<number>;
};

const StyledDialog = styled(Dialog)({
    '& .MuiDialog-paper': {
        width: '100vw',
        height: '100vh',
        maxWidth: 'none',
        maxHeight: 'none',
        margin: 0,
        borderRadius: 0,
        overflow: 'hidden',
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        flexDirection: 'column'
    },
    '& .MuiBackdrop-root': {
        backgroundColor: 'transparent'
    }
});

const CloseButton = styled(IconButton)({
    position: 'absolute',
    top: 12,
    right: 8,
    zIndex: 1,
    color: '#fff',
    '&:hover': {
        color: '#ccc'
    }
});

const ImageGrid = styled('div')<{ imageCount: number }>(({ imageCount }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    gap: '4px',
    '& > .compare-cell': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        ...(imageCount <= 2 ? { width: '49%', height: '98%' } : { width: '49%', height: '49%' })
    }
}));

const CompareImage = styled('img')({
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    display: 'block',
    margin: 'auto',
    userSelect: 'none',
    '-webkit-user-drag': 'none'
} as any);

const SkeletonWrapper = styled('div')({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
});

type CompareGridProps = {
    images: IImageThumbnail[];
};

function CompareGrid({ images }: CompareGridProps) {
    const apiUrl = useApiUrl();
    const [loadedImages, setLoadedImages] = React.useState<Set<number>>(new Set());

    let handleImageLoaded = React.useCallback((imageId: number) => {
        setLoadedImages((prev) => {
            let next = new Set(prev);
            next.add(imageId);
            return next;
        });
    }, []);

    return (
        <ImageGrid imageCount={images.length}>
            {images.map((image) => (
                <div key={image.id} className="compare-cell" style={{ position: 'relative' }}>
                    {!loadedImages.has(image.id) && (
                        <SkeletonWrapper>
                            <Skeleton
                                variant="rectangular"
                                animation="wave"
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                                    '&::after': {
                                        background:
                                            'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)'
                                    }
                                }}
                            />
                            <ImageOutlinedIcon
                                sx={{
                                    position: 'absolute',
                                    zIndex: 1,
                                    fontSize: 48,
                                    color: 'rgba(255, 255, 255, 0.15)'
                                }}
                            />
                        </SkeletonWrapper>
                    )}
                    <CompareImage
                        src={`${apiUrl}/file/${image.previewReference}`}
                        alt={image.filename || `Image ${image.id}`}
                        decoding="async"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                        onLoad={() => {
                            handleImageLoaded(image.id);
                        }}
                        style={!loadedImages.has(image.id) ? { opacity: 0 } : { opacity: 1 }}
                    />
                </div>
            ))}
        </ImageGrid>
    );
}

export function CompareOverlay({ open, onClose, images, selectedImageIds }: CompareOverlayProps) {
    const selectedImages = React.useMemo(
        () => images.filter((img) => selectedImageIds.has(img.id)),
        [images, selectedImageIds]
    );

    return (
        <StyledDialog open={open} onClose={onClose} fullScreen>
            <CloseButton onClick={onClose} aria-label="Close compare view">
                <CloseIcon />
            </CloseButton>

            <CompareGrid images={selectedImages} />
        </StyledDialog>
    );
}
